/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfigurationTransformer, getMessage } from './Validation/ValidationRules';
function useValidation() {
    const [errors, setErrors] = useState({});
    const [isValid, setIsValid] = useState(false);
    const [fieldValidityMap, setFieldValidityMap] = useState({});
    const dataMapRef = useRef({});
    const ruleMapRef = useRef({});
    useEffect(() => {
        let formValid = true;
        //No validation performed (pristine fields)
        if (Object.keys(errors).length === 0) {
            formValid = false;
        }
        else {
            Object.keys(errors).map((key) => {
                if (errors[key]?.length) {
                    formValid = false;
                }
            });
        }
        setIsValid(formValid);
    }, [errors]);
    const applyRules = useCallback((rules, val) => {
        const errors = [];
        rules.map((rule) => {
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            const result = rule.validator(val ? `${val}` : '');
            if (result !== true) {
                const errorMessage = typeof result === 'string' ? result : rule.message ?? 'Invalid value.';
                errors.push(errorMessage);
            }
        });
        return errors;
    }, []);
    const validate = useCallback((key, value) => {
        if (!dataMapRef.current) {
            return false;
        }
        dataMapRef.current[key] = value;
        const currentErrors = applyRules(ruleMapRef.current[key].rules, value);
        setFieldValidityMap((fieldValidityMap) => {
            return {
                ...fieldValidityMap,
                [key]: !currentErrors.length,
            };
        });
        setErrors((errors) => {
            return {
                ...errors,
                [key]: ruleMapRef.current[key].config.firstOnly ? [currentErrors[0]] : currentErrors,
            };
        });
        return currentErrors.length === 0;
    }, [applyRules]);
    const validateField = useCallback((key, value) => {
        if (value === undefined) {
            value = dataMapRef.current[key];
        }
        return validate(key, value);
    }, [validate]);
    const resetValidation = useCallback((key) => {
        setFieldValidityMap((fieldValidityMap) => {
            const newMap = {
                ...fieldValidityMap,
            };
            delete newMap[key];
            return newMap;
        });
        setErrors((errors) => {
            const newMap = {
                ...errors,
            };
            delete newMap[key];
            return newMap;
        });
    }, []);
    const applyValidation = useCallback((key, rulesConfig, config) => {
        ruleMapRef.current[key] = {
            config: config ? config : {},
            rules: getRules(rulesConfig),
        };
        const inlineValidation = (value) => validate(key, value);
        const result = {
            validate: inlineValidation,
            errors: errors[key] ?? [],
            // Only valid if it has been validated
            isValid: fieldValidityMap[key],
            resetValidation: () => resetValidation(key),
        };
        return result;
    }, [errors, validate, fieldValidityMap, resetValidation]);
    const validateFields = useCallback((fields, newValues) => {
        // Allow the calling code to provide the latest values if necessary
        if (newValues) {
            dataMapRef.current = { ...dataMapRef.current, ...newValues };
        }
        let hasErrors = false;
        const newErrors = {};
        fields.forEach((key) => {
            const currentErrors = applyRules(ruleMapRef.current[key].rules, dataMapRef.current[key]);
            newErrors[key] = ruleMapRef.current[key].config.firstOnly ? [currentErrors[0]] : currentErrors;
            if (currentErrors.length > 0) {
                hasErrors = true;
            }
        });
        setErrors(newErrors);
        return !hasErrors;
    }, [applyRules]);
    const validateAll = useCallback((newValues) => {
        if (ruleMapRef.current) {
            return validateFields(Object.keys(ruleMapRef.current), newValues);
        }
        return false;
    }, [validateFields]);
    const clearErrors = () => {
        setErrors(() => {
            return {};
        });
    };
    return { applyValidation, validateAll, validateField, validateFields, errors, isValid, clearErrors };
}
export default useValidation;
function getRules(ruleConfigs) {
    const rules = [];
    Object.keys(ruleConfigs).map((key) => {
        const ruleConfig = ruleConfigs[key];
        if (ruleConfig) {
            const newRule = ConfigurationTransformer[key](ruleConfig);
            if (newRule) {
                rules.push({ validator: newRule, message: getMessage(key, ruleConfig) });
            }
        }
    });
    return rules;
}
//# sourceMappingURL=UseValidation.js.map